import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Artwork from "./Artwork";
import { scrollTo } from "@/lib/scroll";
import { CONTENT } from "@/content";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 38, damping: 18 });
  const sy = useSpring(my, { stiffness: 38, damping: 18 });
  const gx = useTransform(sx, [-0.5, 0.5], [-28, 28]);
  const gy = useTransform(sy, [-0.5, 0.5], [-20, 20]);
  const tx = useTransform(sx, [-0.5, 0.5], [10, -10]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative min-h-screen overflow-hidden flex flex-col"
    >
      <motion.div style={{ x: gx, y: gy }} className="absolute -inset-[6%]">
        <Artwork variant="hero" image={CONTENT.heroImage} alt="TONE — Emotional Techno Artist" breathe glow className="w-full h-full" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#070709]/60 via-transparent to-[#070709]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#070709]/75 via-[#070709]/30 to-transparent" />

      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-12 pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.0 }}
          className="font-mono2 text-[11px] md:text-xs tracking-[0.5em] text-[#a3a3ac] uppercase flex items-center gap-4"
        >
          <span className="inline-block w-10 h-px bg-[#38bdf8]/70" />
          {CONTENT.tagline}
        </motion.p>

        <motion.h1
          data-testid="hero-tone-title"
          style={{ x: tx }}
          className="font-display font-extrabold uppercase leading-[0.82] tracking-[-0.045em] text-[clamp(4rem,16.5vw,20rem)] whitespace-nowrap text-[#f5f3ef] mt-4 select-none"
        >
          {CONTENT.artist.split("").map((ch, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom">
              <motion.span
                className="inline-block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, delay: 0.15 + i * 0.09, ease: EASE }}
              >
                {ch}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
          className="mt-6 text-sm md:text-base text-[#a3a3ac] font-light max-w-md"
        >
          {CONTENT.artistLine}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-music"
            onClick={() => scrollTo("#music")}
            className="px-8 py-3.5 border border-white/15 font-mono2 text-[11px] tracking-[0.3em] text-[#f5f3ef] hover:border-[#38bdf8]/70 hover:text-[#70c7fa] hover:shadow-[0_0_28px_rgba(56,189,248,0.18)] transition-all duration-500"
          >
            MUSIC
          </button>
          <button
            data-testid="hero-cta-shows"
            onClick={() => scrollTo("#shows")}
            className="px-8 py-3.5 border border-white/15 font-mono2 text-[11px] tracking-[0.3em] text-[#f5f3ef] hover:border-[#38bdf8]/70 hover:text-[#70c7fa] hover:shadow-[0_0_28px_rgba(56,189,248,0.18)] transition-all duration-500"
          >
            SHOWS
          </button>
          <button
            data-testid="hero-cta-book"
            onClick={() => scrollTo("#contact")}
            className="px-8 py-3.5 bg-[#f5f3ef] text-[#070709] font-mono2 text-[11px] tracking-[0.3em] font-medium hover:bg-[#70c7fa] hover:shadow-[0_0_36px_rgba(56,189,248,0.35)] transition-all duration-500"
          >
            BOOK TONE
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.7 }}
        className="relative z-10 flex items-end justify-between px-6 md:px-12 pb-8 font-mono2 text-[10px] tracking-[0.3em] text-[#64646e] uppercase"
      >
        <span>{CONTENT.base}</span>
        <span className="hidden md:flex flex-col items-center gap-3">
          <span>SCROLL</span>
          <span className="block w-px h-12 bg-[#38bdf8]/60 animate-scroll-hint" />
        </span>
        <span>{CONTENT.instagram.handle}</span>
      </motion.div>
    </section>
  );
}
